import { Grid } from '@mui/material';
import React from 'react';
import {
  FileDropZone,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import InfoBar from '../../../atoms/InfoBar';
import { scrollBarStyles } from '../../../../core-utils/messages/CommonConstants';
import { bytesToMB } from '../../../../core-utils/helper-utils/contractAIQHelper';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()({
  uploadFilesGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '48px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
      marginBottom: '24px',
    },
  },
  uploadedFiles: {
    height: '233px',
    width: '60%',
    background: patientAccessTheme.palette.background.default,
    borderRadius: '4px',
    padding: '18px 12px',
    boxSizing: 'border-box',
    '@media (max-width: 768px)': { width: '100%' },
  },
  files: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    height: '136px',
    overflow: 'auto',
    paddingRight: '8px',
    ...scrollBarStyles,
  },
});

export interface UploadFilesSectionProps {
  uploadedFiles: any[];
  setUploadedFiles: React.Dispatch<any>;
  /** info text shown in the neutral bar at the bottom */
  uploadInfo: string;
  /** max file size in MB, used in the oversize message */
  maxFileMB: number;
  /** duplicate file names detected in the current selection */
  duplicateError?: string[];
  /** oversize file names detected in the current selection */
  oversizeError?: string[];
  /** allow selecting multiple files (contracts = true, single = false) */
  isMultiple?: boolean;
}

const UploadFilesSection: React.FC<UploadFilesSectionProps> = ({
  uploadedFiles,
  setUploadedFiles,
  uploadInfo,
  maxFileMB,
  duplicateError = [],
  oversizeError = [],
  isMultiple = true,
}) => {
  const { classes } = useStyles();

  return (
    <>
      <Grid className={classes.uploadFilesGrid}>
        {uploadedFiles?.length > 0 && (
          <Grid className={classes.uploadedFiles}>
            <Typography
              fontSize="12px"
              fontType="regular"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.text.lowemphasis}
              margin="0px 0px 16px 0px"
            >
              {`${uploadedFiles.length} Document${
                uploadedFiles.length > 1 ? 's' : ''
              } uploaded`}
            </Typography>
            <Grid className={classes.files}>
              {uploadedFiles.map((item: any, index: number) => (
                <UploadedDocument
                  key={`${item?.name}-${index}`}
                  name={item?.name}
                  size={`${bytesToMB(item?.size)} MB`}
                  handleDelete={() => {
                    const updatedItems = [...uploadedFiles];
                    updatedItems.splice(index, 1);
                    setUploadedFiles(updatedItems);
                  }}
                  width="100%"
                  truncateWidth="300px"
                />
              ))}
            </Grid>
          </Grid>
        )}
        <FileDropZone
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          filesSupported={['.pdf']}
          width={uploadedFiles?.length > 0 ? '38%' : '100%'}
          height="233px"
          isMultiple={isMultiple}
        />
      </Grid>

      {duplicateError.length > 0 && (
        <InfoBar
          type="Error"
          height="100%"
          width="100%"
          info={
            <>
              {`Duplicate file name${
                duplicateError.length > 1 ? 's' : ''
              } detected. More than one file has the same name: `}
              <span style={{ fontWeight: 600 }}>
                {duplicateError.join(', ')}
              </span>
              {'. Please rename or remove the duplicates and try again.'}
            </>
          }
        />
      )}

      {oversizeError.length > 0 && (
        <InfoBar
          type="Error"
          height="100%"
          width="100%"
          info={
            <>
              {`The following file${
                oversizeError.length > 1 ? 's' : ''
              } exceed${
                oversizeError.length > 1 ? '' : 's'
              } the maximum size of ${maxFileMB} MB: `}
              <span style={{ fontWeight: 600 }}>
                {oversizeError.join(', ')}
              </span>
              {`. Please remove ${
                oversizeError.length > 1 ? 'these files' : 'the file'
              } and try again.`}
            </>
          }
        />
      )}

      <InfoBar info={uploadInfo} type="Info" width="100%" />
    </>
  );
};

export default UploadFilesSection;
